import { Helmet } from 'react-helmet'
import { Navigate, useNavigate } from 'react-router-dom'
import { getChainById } from 'symbiosis-js-sdk'

import SISIcon from '~/assets/icons/sis.svg'
import { Banner } from '~/components/Banner'
import { PageHeader } from '~/components/PageHeader'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'
import { SymbiosisButton } from '~/components/SymbiosisButton'
import { SymbiosisInput } from '~/components/SymbiosisInput'
import Tile from '~/components/Tile'
import { useLockDetails } from '~/lib/useLockDetails'
import { useWallet } from '~/lib/useWallet'

import { Details } from './Details'
import { useApproveSISToken } from './useApproveSISToken'
import { useConfirmLockParams } from './useConfirmLockParams'
import { useCreateLock } from './useCreateLock'
import { getApproveButtonText, getCreateButtonText } from './utils'

import { useVESISParams } from '~/hooks/useVESISParams'
import { patchExpiresDate } from '~/lib/patchExpiresDate'
import styles from './ConfirmLockPage.pcss'

export function ConfirmLockPage() {
	const { config } = useVESISParams()
	const navigate = useNavigate()

	const wallet = useWallet()

	const { data: lock, isFetched, isLoading } = useLockDetails()

	const { amount, amountBN, expires, weeks } = useConfirmLockParams()

	const { needApprove, isApproving, approve } = useApproveSISToken(amountBN)
	const { isPending: isCreatingLock, mutate: createLock } = useCreateLock(
		amountBN,
		weeks
	)

	const chain = getChainById(wallet.chainId)

	if (!amountBN || !weeks || (lock && isFetched)) {
		return <Navigate to={`/vesis/${config.slug}`} />
	}

	return (
		<Tile>
			<Helmet>
				<title>Confirm Lock</title>
			</Helmet>

			<Tile.Header>
				<PageHeader
					title="Confirm"
					backLink={`/vesis/${config.slug}/create?amount=${amount}&weeks=${weeks}`}
					onQuestionClick={() => {
						window.open(
							'https://docs.symbiosis.finance/liquidity-providers/vesis',
							'_blank'
						)
						// TODO show bubbles
					}}
				/>
			</Tile.Header>

			<div className={styles.content}>
				<SymbiosisInput
					inputStyle={styles.input}
					address={wallet.account}
					label="Amount"
					networkIcon={chain?.icons?.small}
					placeholder="Enter SIS Amount"
					startAdornment={<SISIcon />}
					value={amount}
					disabled
					endAdornment="SIS"
				/>
				<div className={styles.date}>
					<span>Expires Date:</span>
					<span>
						{patchExpiresDate(config, expires).toLocaleString()}
					</span>
				</div>

				<Banner className={styles.banner}>
					You will also receive extra rewards in wBTC, wETH, and USDC.
					Claim will be available once a month.
				</Banner>

				<Details
					className={styles.details}
					amount={amountBN}
					duration={weeks}
				/>

				<SymbiosisButton
					variant="white"
					disabled={!needApprove || isApproving}
					onClick={() => {
						approve()
					}}
				>
					{getApproveButtonText(isApproving, needApprove)}
				</SymbiosisButton>
			</div>

			<Tile.Footer>
				<SwitchNetworkButton
					block
					color="black"
					disabled={needApprove || isCreatingLock || isLoading}
					onClick={() => {
						createLock(undefined, {
							onSuccess() {
								navigate(`/vesis/${config.slug}`)
							},
						})
					}}
					requiredChainId={config.chain.id}
				>
					{getCreateButtonText(isCreatingLock, needApprove)}
				</SwitchNetworkButton>
			</Tile.Footer>
		</Tile>
	)
}
